/**
 * interactive-deduction (Phase 3-3, Task 6.6) PlayerScreen 統合用 Host。
 *
 * PlayerScreen から scene / playline 情報を受け取り、scene.contradictions が non-empty
 * かつ feature flag on のときだけ DeductionPanel をモーダル表示する。
 * 成功 / forcedBadEnding 応答は親 callback に中継し、親が playline state を更新する。
 *
 * Req 4.4 / 5.4 / 12.4 準拠。
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useMemories } from "../../api/hooks/useMemories";
import type { DeductionResponse } from "../../api/hooks/useDeduction";
import type { components } from "../../api/schema";
import type { UIError } from "../../errors/types";
import { DeductionPanel, type DeductionStyle } from "./DeductionPanel";
import type { DeductionPanelContradiction, EvidenceFragment } from "./types";

type Scene = components["schemas"]["Scene"];
type MemoryFragment = components["schemas"]["MemoryFragment"];

export type DeductionHostProps = {
  enabled: boolean;
  playlineId: string;
  sceneId: string;
  scene: Scene;
  /** Playline.deductionAttempts (駆け引き無効なら null) */
  attempts: number | null | undefined;
  /** preset.interactiveDeduction.style。未指定なら class-trial に fallback */
  style: DeductionStyle | string;
  /** 成功 / forcedBadEnding 応答時 (nextSceneId あり)。親は current scene を refetch する */
  onResolved: (result: DeductionResponse) => void;
  /** forcedBadEnding=true のとき palimpsest 誘導 UI を出すため親に通知 */
  onForcedBadEnding?: () => void;
  /** エラー時 banner 表示用 */
  onError?: (err: UIError) => void;
  /**
   * Panel がアクティブ (描画中 = 未解決の contradiction あり) かを親に通知する。
   * PlayerScreen 側は palimpsest と同じ paused 連動で StreamingSceneView の typewriter を停止する
   * (Task 6.6 Req)。
   */
  onActiveChange?: (active: boolean) => void;
};

function toEvidenceFragment(m: MemoryFragment): EvidenceFragment {
  // NOTE: 現 OpenAPI schema の MemoryFragment は (isEvidence / evidenceTag) を expose して
  //       いない (server 側 MemoryFragmentInputZ 拡張のみ)。label は key を流用。
  //       Phase 3-3+ で MemoryFragment response schema にも isEvidence/evidenceTag を
  //       伝搬する follow-up を予定。MVP では全件 isEvidence=undefined 相当で表示する。
  return {
    key: m.key,
    label: m.key,
  };
}

function toPanelContradiction(
  c: NonNullable<Scene["contradictions"]>[number],
): DeductionPanelContradiction {
  return {
    id: c.id,
    text: c.text,
    expectedMemoryKey: c.expectedMemoryKey,
    ...(c.hint !== undefined && c.hint !== null ? { hint: c.hint } : {}),
  };
}

export function DeductionHost({
  enabled,
  playlineId,
  sceneId,
  scene,
  attempts,
  style,
  onResolved,
  onForcedBadEnding,
  onError,
  onActiveChange,
}: DeductionHostProps): JSX.Element | null {
  const queryClient = useQueryClient();
  // resolved: 成功 OR forcedBadEnding のときのみ true。plain failure (success=false かつ
  // forcedBadEnding=false) では再挑戦のため panel を維持する (Task 6.6 Req "誤答 → panel 残る")
  const [resolved, setResolved] = useState(false);

  const memoriesQuery = useMemories(playlineId);

  const contradictions = scene.contradictions ?? [];
  const active =
    enabled && contradictions.length > 0 && !resolved;

  // 親 (PlayerScreen) に active 状態を通知 → StreamingSceneView.paused と連動
  useEffect(() => {
    onActiveChange?.(active);
    return () => {
      onActiveChange?.(false);
    };
  }, [active, onActiveChange]);

  // Req 12.4: feature flag off で DeductionPanel 関連 DOM 一切出さない
  if (!enabled) return null;

  // shouldFire 相当の client-side 判定: contradictions が non-empty のみ
  if (contradictions.length === 0) return null;

  // 解決済み (成功 / 強制 bad ending) ならスキップ
  if (resolved) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
      data-testid="deduction-modal"
    >
      <div className="w-full max-w-lg">
        <DeductionPanel
          style={style}
          playlineId={playlineId}
          sceneId={sceneId}
          contradictions={contradictions.map(toPanelContradiction)}
          memories={(memoriesQuery.data ?? []).map(toEvidenceFragment)}
          attempts={attempts}
          onResolved={(result) => {
            // 成功 OR forcedBadEnding のときだけ panel を閉じる。plain 失敗は retry 継続。
            const isTerminal = result.success || result.forcedBadEnding;
            if (isTerminal) {
              setResolved(true);
              if (result.forcedBadEnding) {
                onForcedBadEnding?.();
              }
              queryClient.invalidateQueries({
                queryKey: ["currentScene", playlineId],
              });
              queryClient.invalidateQueries({
                queryKey: ["memories", playlineId],
              });
            } else {
              // 失敗時は memories cache だけ invalidate (attempts が変わる可能性)
              queryClient.invalidateQueries({
                queryKey: ["memories", playlineId],
              });
            }
            onResolved(result);
          }}
          onError={onError}
        />
      </div>
    </div>
  );
}
